import { Injectable, signal } from "@angular/core";
import { v4 } from "uuid";
import { Device } from "@capacitor/device";
import { DevModeService } from "./dev.mode.service";
import { LogRepository } from "../repositories/log.repository";
import { EnvironmentService } from "./environment.service";
import { ConsoleTransport } from "./logger.console.transport";
import { AuthRepository } from "../repositories/auth.repository";
import { DownloadedClientRepository } from "../repositories/downloaded.client.repository";
import { handlePromise } from "../../utils";
import { StorageTransport } from "./logger.storage.transport";
import { LogTransports } from "../types/Log/LogTransports";
import { LogLevels } from "../types/Log/LogLevels";
import { Logger } from "../types/Log/Logger";
import { Log } from "../types/Log/Log";
import { ProcessedLog } from "../types/Log/ProcessedLog";

@Injectable({
  providedIn: "root",
})
export class LogService {
  public logs = signal<ProcessedLog[]>([]);

  public defaultMeta = {};

  constructor(
    private environmentService: EnvironmentService,
    private logRepo: LogRepository,
    private downloadedClientRepo: DownloadedClientRepository,
    private AuthRepo: AuthRepository,
    private devModeService: DevModeService,
  ) {
    this.addTransports();
  }

  private loggers: { [key: string]: any } = {};

  private transports: { [key: string]: any } = {};

  public logQueue: Log[] = [];

  public processingQueue = false;

  private globalConfig = {
    service: "LogService",
  };

  public async init() {
    await this.updateMeta();
    const logs = await this.logRepo.get();
    this.logs.set(logs);
  }

  public async getLogs(
    fresh: boolean = false,
    numberOfLogs: number | undefined = undefined,
  ) {
    if (fresh) {
      const freshLogs = await this.logRepo.get();
      return freshLogs.slice(0, numberOfLogs);
    }
    return this.logs().slice(0, numberOfLogs);
  }

  public setLogs(logs: ProcessedLog[]) {
    this.logs.set(logs);
    return this.logRepo.set(logs);
  }

  public async removeLogs(numberOfLogs: number | undefined = undefined) {
    const logs = await this.getLogs();
    const logsToRemove = logs.slice(0, numberOfLogs);
    const remainingLogs = await this.logRepo.removeMultiple(
      "logID",
      logsToRemove.map((log) => log.logID),
    );
    this.logs.set(remainingLogs);
    return remainingLogs;
  }

  public async getUserMeta() {
    const [, userRes] = await handlePromise(this.AuthRepo.get());
    if (userRes) {
      const { env, email, userID } = userRes;
      return { env, email, userID };
    }
    return {};
  }

  public async getClientMeta(clientMeta?: any) {
    if (clientMeta) {
      const { dsn, clientName, clientID } = clientMeta;
      return { dsn, clientName, clientID };
    }
    const [, clientRes] = await handlePromise(
      this.downloadedClientRepo.getCurrentClient(),
    );
    if (clientRes) {
      const { dsn, clientName, clientID } = clientRes;
      return { dsn, clientName, clientID };
    }
    return {};
  }

  public async updateMeta(clientMeta?: any) {
    const device = await Device.getInfo();
    const user = await this.getUserMeta();
    const client = await this.getClientMeta(clientMeta);
    const meta = {
      user,
      client,
      device,
      appVersion: this.environmentService.getVersion(),
    };
    this.defaultMeta = meta;
    return this.defaultMeta;
  }

  public async updateAllLoggersMeta(client?: any) {
    const newMeta = await this.updateMeta(client);
    this.updateAllLoggers(newMeta);
  }

  private setLogLevel(): LogLevels {
    if (this.devModeService.isDevMode()) {
      const devLevel = this.environmentService.getLogLevel();
      return devLevel;
    }
    return "ERROR";
  }

  public addTransports() {
    if (this.devModeService.isDevMode()) {
      const logTransports = this.environmentService.getLogTransports();
      const transports = logTransports?.split?.(",") || [];
      transports.forEach((transport) => {
        if (transport === "console") {
          this.addConsoleTransport();
        }
        if (transport === "storage") {
          this.addStorageTransport();
        }
      });
    } else {
      this.addStorageTransport();
    }
  }

  public addStorageTransport() {
    if (!this.transports[LogTransports.STORAGE]) {
      this.transports[LogTransports.STORAGE] = new StorageTransport(
        this.logRepo,
        this.logs,
      );
    }
  }

  public addConsoleTransport() {
    if (!this.transports[LogTransports.CONSOLE]) {
      this.transports[LogTransports.CONSOLE] = new ConsoleTransport();
    }
  }

  public getTransports() {
    return this.transports;
  }

  public getTransport(transportName: LogTransports) {
    return this.transports[transportName];
  }

  public removeTransport(transportName: LogTransports) {
    delete this.transports[transportName];
  }

  public clearTransports() {
    this.transports = {};
  }

  private triggerLogProcessing() {
    if (this.processingQueue) {
      return;
    }
    this.processLogQueue();
  }

  public processLogQueue() {
    if (this.logQueue.length > 0) {
      this.processingQueue = true;
      const log = this.logQueue.shift();
      if (log) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.processLog(log.message, log.additional, log.meta, log.level);
      }
    }
  }

  public async processLog(
    message: string | Error | object,
    additional: string | Error | object,
    meta: any,
    level: LogLevels,
  ) {
    const logID = v4();
    const dts = Date.now();
    await Promise.all(
      Object.values(this.transports).map((transport: any) => {
        return transport.log({
          message,
          additional,
          ...meta,
          logID,
          level,
          dts,
        });
      }),
    );
    if (this.logQueue.length > 0) {
      this.processLogQueue();
    }
    this.processingQueue = false;
  }

  private filterLog(log: any, allowedLevel: LogLevels) {
    const severityMap: Record<LogLevels, number> = {
      SILENT: 3,
      ERROR: 2,
      WARN: 1,
      INFO: 0,
    };
    const logSeverity = severityMap[log.level as LogLevels];
    const allowedSeverity = severityMap[allowedLevel];
    return logSeverity >= allowedSeverity;
  }

  private addLogToQueue(log: any, allowedLevel: LogLevels) {
    if (this.filterLog(log, allowedLevel)) {
      this.logQueue.push(log);
      this.triggerLogProcessing();
    }
  }

  private createLogger(
    initialMeta: { service: string; [key: string]: any },
    allowedLevel: LogLevels,
  ): Logger {
    const logger: Logger = {
      meta: { ...initialMeta },
      log: (level: LogLevels, message: any, additional: any) =>
        this.addLogToQueue(
          {
            message,
            additional,
            meta: logger.meta,
            level,
          },
          allowedLevel,
        ),
      info: (message: any, additional: any) =>
        logger.log("INFO", message, additional),
      warn: (message: any, additional: any) =>
        logger.log("WARN", message, additional),
      error: (message: any, additional: any) =>
        logger.log("ERROR", message, additional),
    };
    this.loggers[initialMeta.service] = logger;
    return logger;
  }

  public updateAllLoggers(meta: { [key: string]: any }) {
    Object.values(this.loggers).forEach((logger: Logger) => {
      // eslint-disable-next-line no-param-reassign
      logger.meta = { ...logger.meta, ...meta };
    });
  }

  public unRegisterLogger(service: string) {
    delete this.loggers[service];
  }

  public getLogger(service: string) {
    return this.loggers[service];
  }

  public clearLoggers() {
    this.loggers = {};
  }

  public registerLogger(meta: { service: string; [key: string]: any }): Logger {
    const logger = this.getLogger(meta.service);
    if (logger) {
      logger.meta = { ...logger.meta, ...this.defaultMeta, ...meta };
      return logger;
    }
    return this.createLogger(
      { ...this.globalConfig, ...this.defaultMeta, ...meta },
      this.setLogLevel(),
    );
  }
}
